import { Home, Plus, Eye, Settings, MessageSquare, Lock, ClipboardList, Mail, BarChart2, FileSearch, ChevronRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Link, useLocation, useSearch } from "wouter";
import { ACTIVE_TASK_TEMPLATE_COUNT } from "@shared/constants/task-count";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuAction,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { getIcon } from "@/hooks/use-categories";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { ClientHiddenCategory } from "@shared/schema";
import { useEffect, useMemo, useState } from "react";
import { useUpgradeModal } from "@/contexts/UpgradeModalContext";
import { HIDDEN_SECTIONS } from '@shared/constants/section-affordances';
import { CLAY, PRODUCT_NAME, SAGE } from "@shared/brand";

interface UnifiedCategory {
  id: string;
  slug: string;
  name: string;
  icon: string;
  categoryType: string;
  sortOrder: number;
  completed: number;
  total: number;
  sectionCount: number;
}

interface AppSidebarProps {
  clientId?: string;
  plannerTier?: string;
  onNewClient?: () => void;
  onAddCategory?: () => void;
  readOnly?: boolean;
}

interface SidebarSection {
  id: string;
  slug: string;
  name: string;
  isActive?: boolean;
}

// A section-bearing category row: the label navigates to the category; the
// persistent chevron expands an indented, lazily-fetched list of that category's
// sections. Each section links to /category/:slug?section=<sectionSlug>, which the
// category page reads to open + scroll to that section.
function CategoryNavItem({
  item,
  location,
}: {
  item: { title: string; url: string; icon: any; categoryId?: string };
  location: string;
}) {
  const search = useSearch();
  const currentSection = new URLSearchParams(search).get('section');
  // Sidebar = where you are: navigating to a category auto-expands its section
  // tree (route-derived). On top of that, a local override lets the user manually
  // collapse the active category's tree via its chevron; navigating to a different
  // category clears the override so the new route drives expansion again.
  const isActiveCategory = location === item.url;
  const [userClosed, setUserClosed] = useState(false);
  const open = isActiveCategory && !userClosed;

  // Clear the manual-collapse override whenever we leave this category, so
  // returning to it re-opens the tree (route derivation resumes).
  useEffect(() => {
    if (!isActiveCategory) setUserClosed(false);
  }, [isActiveCategory]);

  // Lazy: only fetch this category's sections once it's expanded.
  const { data: sections = [] } = useQuery<SidebarSection[]>({
    queryKey: ["/api/generic-categories", item.categoryId, "sections"],
    queryFn: async () => {
      if (!item.categoryId) return [];
      const res = await fetch(`/api/generic-categories/${item.categoryId}/sections`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: open && !!item.categoryId,
  });

  // Same visibility rule the category page applies (the endpoint does not filter).
  const visibleSections = useMemo(
    () => sections.filter(s => s.isActive !== false && !HIDDEN_SECTIONS.has(s.slug)),
    [sections],
  );

  // Both the name and the chevron navigate to the category. Re-clicking the
  // already-active category is a same-path no-op for wouter, so it instead signals
  // the category page to collapse its open section (reuses the s78 coupling).
  const navigateOrCollapse = () => {
    if (location === item.url) {
      window.dispatchEvent(new CustomEvent('oe:collapse-sections', { detail: { categoryUrl: item.url } }));
    }
  };

  return (
    // `open` is derived from the active route, so the Collapsible is fully
    // controlled and onOpenChange is a no-op (navigation drives expansion).
    <Collapsible open={open} onOpenChange={() => {}} asChild>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={location === item.url}
          data-testid={`link-${item.url.substring(1).replace(/\//g, '-')}`}
        >
          <Link href={item.url} onClick={navigateOrCollapse}>
            <item.icon className="h-4 w-4 text-primary" />
            <span>{item.title}</span>
          </Link>
        </SidebarMenuButton>
        {/* The chevron navigates to the same category as the name. Hidden from the
            a11y tree + tab order so screen readers don't announce a duplicate link. */}
        <SidebarMenuAction asChild data-testid={`button-toggle-sections-${item.categoryId}`}>
          <Link
            href={item.url}
            onClick={(e) => {
              // Already on this category → the chevron is a pure collapse/expand
              // toggle (navigating would be a same-path no-op). Otherwise let the
              // link navigate, which auto-expands the tree via the route.
              if (isActiveCategory) {
                e.preventDefault();
                setUserClosed((c) => !c);
              }
            }}
            aria-hidden="true"
            tabIndex={-1}
          >
            <ChevronRight className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`} />
          </Link>
        </SidebarMenuAction>
        <CollapsibleContent>
          <SidebarMenuSub>
            {visibleSections.map((s) => (
              <SidebarMenuSubItem key={s.id}>
                <SidebarMenuSubButton
                  asChild
                  isActive={location === item.url && currentSection === s.slug}
                  size="sm"
                  className="pl-4 text-sidebar-section-foreground"
                >
                  <Link href={`${item.url}?section=${s.slug}`}>
                    <span>{s.name}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

export function AppSidebar({ clientId, plannerTier = "essential", onNewClient, onAddCategory, readOnly = false }: AppSidebarProps) {
  const [location] = useLocation();
  const { openUpgradeModal } = useUpgradeModal();
  const { toast } = useToast();
  const [hideCategoryTarget, setHideCategoryTarget] = useState<{ id: string; name: string } | null>(null);

  // Fetch unified categories from the progress API
  const { data: unifiedCategories = [] } = useQuery<UnifiedCategory[]>({
    queryKey: ["/api/unified/progress", clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const res = await fetch(`/api/unified/progress?clientId=${clientId}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!clientId,
  });

  // Fetch hidden categories
  const { data: hiddenCategories = [] } = useQuery<ClientHiddenCategory[]>({
    queryKey: ["/api/clients", clientId, "hidden-categories"],
    queryFn: async () => {
      if (!clientId) return [];
      const res = await fetch(`/api/clients/${clientId}/hidden-categories`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!clientId,
  });

  const hiddenCategoryIds = useMemo(() => {
    return new Set(hiddenCategories.map(hc => hc.categoryId));
  }, [hiddenCategories]);

  // Mutation to hide a category
  const hideCategoryMutation = useMutation({
    mutationFn: async (categoryId: string) => {
      if (!clientId) throw new Error("No client selected");
      const res = await apiRequest("POST", `/api/clients/${clientId}/hidden-categories`, { categoryId });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "hidden-categories"] });
      setHideCategoryTarget(null);
      toast({
        title: "Category hidden",
        description: "You can restore hidden categories from the Dashboard.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error hiding category",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const unhideCategoryMutation = useMutation({
    mutationFn: async (categoryId: string) => {
      if (!clientId) throw new Error("No client selected");
      await apiRequest("DELETE", `/api/clients/${clientId}/hidden-categories/${categoryId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "hidden-categories"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error restoring category", description: error.message, variant: "destructive" });
    },
  });

  const handleHideCategory = (e: React.MouseEvent, categoryId: string, categoryName: string) => {
    e.preventDefault();
    e.stopPropagation();
    setHideCategoryTarget({ id: categoryId, name: categoryName });
  };

  // All categories use the unified route
  const getCategoryUrl = (cat: UnifiedCategory): string => {
    return `/category/${cat.slug}`;
  };

  // Build nav items with categoryId for hideable items
  const navItems: Array<{
    title: string;
    url: string;
    icon: any;
    categoryId?: string;
    canHide: boolean;
    featured?: boolean;
    completed?: number;
    total?: number;
    sectionCount?: number;
    isSubItem?: boolean;
  }> = [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: Home,
      canHide: false,
    },
    ...unifiedCategories
      .filter(cat => !hiddenCategoryIds.has(cat.id))
      .map((cat) => ({
        title: cat.name,
        url: getCategoryUrl(cat),
        icon: getIcon(cat.icon),
        categoryId: cat.id,
        canHide: true,
        completed: cat.completed,
        total: cat.total,
        sectionCount: cat.sectionCount,
      })),
  ];

  const hiddenNavItems = unifiedCategories
    .filter(cat => hiddenCategoryIds.has(cat.id));

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <div className="flex flex-col gap-1.5">
          <Link href="/">
            <span className="flex items-center gap-2 cursor-pointer group" data-testid="link-sidebar-home">
              <svg width="28" height="28" viewBox="0 0 64 64" fill="none" aria-hidden="true" style={{ flex: "none" }}>
                <circle cx="32" cy="32" r="24" stroke={SAGE[600]} strokeWidth="3" />
                <path d="M32 46V24" stroke={SAGE[600]} strokeWidth="3" strokeLinecap="round" />
                <path d="M32 30l-7-5.5M32 30l7-5.5M32 38l-6-4.5M32 38l6-4.5" stroke={SAGE[600]} strokeWidth="3" strokeLinecap="round" />
              </svg>
              <h2 className="text-lg font-semibold group-hover:text-primary transition-colors">{PRODUCT_NAME}</h2>
            </span>
          </Link>
          <Link href="/">
            <span className="text-sm text-muted-foreground hover:text-primary cursor-pointer transition-colors" data-testid="link-sidebar-home-text">
              Home
            </span>
          </Link>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>My Estate Plan</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                // Multi-section categories get the collapsible section tree;
                // single-section categories (e.g. About You, Where Is Everything) and
                // the fixed Dashboard item render flat.
                const navKey = `${item.url}::${item.title}`;
                if (item.categoryId && (item.sectionCount ?? 0) > 1) {
                  return (
                    <CategoryNavItem
                      key={navKey}
                      item={item}
                      location={location}
                    />
                  );
                }
                return (
                <SidebarMenuItem key={navKey}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.url}
                    className={item.featured ? "font-semibold" : ""}
                    style={item.featured ? { backgroundColor: 'hsl(168 76% 42% / 0.1)' } : undefined}
                    data-testid={`link-${item.url.substring(1).replace(/\//g, '-') || 'dashboard'}`}
                  >
                    <Link
                      href={item.url}
                      className={item.isSubItem ? "pl-6" : undefined}
                      onClick={() => {
                        // Re-clicking the already-active category collapses its open
                        // section (same-path no-op for wouter — see CategoryNavItem).
                        if (item.categoryId && location === item.url) {
                          window.dispatchEvent(new CustomEvent('oe:collapse-sections', { detail: { categoryUrl: item.url } }));
                        }
                      }}
                    >
                      <item.icon className={item.featured ? "h-5 w-5 text-primary fill-primary" : "h-4 w-4 text-primary"} />
                      <span className={item.featured ? "text-primary text-base" : ""}>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {!readOnly && onAddCategory && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton onClick={onAddCategory} className="text-sm text-muted-foreground hover:text-foreground">
                    <Plus className="h-4 w-4 text-muted-foreground" />
                    <span>+ Add a category</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {/* ── Statement Scanner, planner-side (s217, STATEMENT-SCANNER-PLANNER-1) ───────
            🔴 This is a SCANNER-ONLY entry, deliberately NOT the `{false && ...}` TOOLS
            zone below turned back on. That zone carries three items — Financial Summary,
            Statement Scanner and Letter Generator — and D-216.4 unblocked the scanner's
            planner home, not the zone. Flipping the literal would have shipped two
            unrelated surfaces as a side effect of this build.

            The route is the PLANNER one (`/statement-scanner`), not the executor's
            `/executor/:clientId/statement-scanner`: same page, mode="planner", and no
            death-verification gate (the server's gate is scoped to `role === "executor"`,
            so a planner scanning their own statements was never gated).

            🔴 NO TIER LOCK, and that is a decision worth seeing rather than inferring.
            The zone below greys these items for anyone under the Complete tier, but
            `plannerTier` appears in no route guard anywhere — the paywall is navigation
            only. Locking the scanner here would hide the build from every `essential`
            planner, which is who the three-months step is for. Reported for a ruling. */}
        {!readOnly && clientId && (
          <SidebarGroup data-testid="sidebar-zone-planner-tools">
            <SidebarGroupLabel>TOOLS</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location === "/statement-scanner"}>
                    <Link href="/statement-scanner" data-testid="sidebar-statement-scanner">
                      <FileSearch className="h-4 w-4 text-primary" />
                      <span>Statement Scanner</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {/* Tools — tier/paywall lock (Complete tier). Projections of the estate data,
            usable by planner + executor (family-meeting use case). The tier lock is the
            warm tan "LOCKED" badge + greyed items that open the upgrade modal. */}
        {/* HIDDEN: F&F — executor/tools sections hidden until executor build ships */}
        {false && !readOnly && clientId && (
          <SidebarGroup data-testid="sidebar-zone-tools">
            <SidebarGroupLabel className="flex items-center gap-2">
              TOOLS
              {plannerTier !== "complete" && (
                <span
                  className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                  style={{ backgroundColor: CLAY[100], color: CLAY[800] }}
                  data-testid="tools-tier-lock-badge"
                >
                  LOCKED
                </span>
              )}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {plannerTier !== "complete" ? (
                  <>
                    {([
                      { title: "Financial Summary", icon: BarChart2 },
                      { title: "Statement Scanner", icon: FileSearch },
                      { title: "Letter Generator",  icon: Mail },
                      // "Estate Documents" hidden s90: the page is executor-only +
                      // death-verified on the server, so it's unusable for a planner's
                      // own estate. The /executor/:clientId/estate-documents route stays
                      // registered for client users who are executors for someone else.
                    ] as Array<{ title: string; icon: React.ComponentType<{ className?: string }> }>).map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          className="opacity-50 cursor-not-allowed"
                          onClick={() => openUpgradeModal({ triggerSource: "sidebar", toolName: item.title })}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                    <SidebarMenuItem>
                      <div className="px-2 pt-1">
                        <button
                          className="w-full text-sm font-medium py-2 px-3 rounded-md text-center transition-colors bg-[#C7906D] text-[#3A2414] hover:bg-[#A56E4B] hover:text-[#F5E6DB]"
                          onClick={() => openUpgradeModal({ triggerSource: "sidebar-unlock-button" })}
                        >
                          Unlock with Complete
                        </button>
                      </div>
                    </SidebarMenuItem>
                  </>
                ) : (
                  <>
                    {([
                      { title: "Financial Summary", icon: BarChart2,  url: `/executor/${clientId}/estate-summary` },
                      { title: "Statement Scanner", icon: FileSearch, url: `/executor/${clientId}/statement-scanner` },
                      { title: "Letter Generator",  icon: Mail,       url: `/executor/${clientId}/letters` },
                      // "Estate Documents" hidden s90 — see note in the locked-tier block above.
                    ] as Array<{ title: string; icon: React.ComponentType<{ className?: string }>; url: string }>).map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          asChild
                          isActive={location === item.url}
                        >
                          <Link href={item.url}>
                            <item.icon className="h-4 w-4 text-primary" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {/* For your executor — handoff lock (Option A, s82). These settlement surfaces
            unlock when the executor takes over at death; on the planner's live sidebar
            they are ALWAYS locked and non-interactive (no upgrade path — nothing to buy).
            Visually distinct from the tier lock: cool muted styling, Lock icon, an
            "AT HANDOFF" badge, and a static helper line instead of a CTA button. */}
        {/* HIDDEN: F&F — executor/tools sections hidden until executor build ships */}
        {false && !readOnly && clientId && (
          <SidebarGroup data-testid="sidebar-zone-executor">
            <SidebarGroupLabel className="flex items-center gap-2">
              FOR YOUR EXECUTOR
              <span
                className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium bg-muted text-muted-foreground"
                data-testid="executor-handoff-lock-badge"
              >
                <Lock className="h-3 w-3" />
                AT HANDOFF
              </span>
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {([
                  { title: "Settle the Estate",    icon: ClipboardList },
                  { title: "Task List",            icon: ClipboardList, badge: String(ACTIVE_TASK_TEMPLATE_COUNT) },
                  { title: "Review with Executor", icon: MessageSquare },
                ] as Array<{ title: string; icon: React.ComponentType<{ className?: string }>; badge?: string }>).map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      className="opacity-50 cursor-default text-muted-foreground"
                      aria-disabled="true"
                      tabIndex={-1}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                      {item.badge && (
                        <span
                          className="ml-auto text-xs font-semibold rounded-full px-1.5 py-0.5 shrink-0"
                          style={{ backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}
                        >
                          {item.badge}
                        </span>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
                <SidebarMenuItem>
                  <div className="px-2 pt-1 text-xs text-muted-foreground">
                    Unlocks when your executor takes over.
                  </div>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {!readOnly && hiddenNavItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs text-muted-foreground">Hidden Categories</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {hiddenNavItems.map((cat) => (
                  <SidebarMenuItem key={cat.id}>
                    <SidebarMenuButton className="opacity-50 text-muted-foreground text-sm">
                      <span className="truncate">{cat.name}</span>
                    </SidebarMenuButton>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SidebarMenuAction
                          showOnHover
                          onClick={() => unhideCategoryMutation.mutate(cat.id)}
                          aria-label={`Restore ${cat.name}`}
                          data-testid={`button-unhide-category-${cat.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </SidebarMenuAction>
                      </TooltipTrigger>
                      <TooltipContent side="right">Restore to sidebar</TooltipContent>
                    </Tooltip>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
      {/* Pinned footer — sits OUTSIDE the scrolling SidebarContent so Settings is
          always visible regardless of how tall the nav/Tools/executor groups above
          it grow (IA §2: Settings pinned bottom). Settings keeps its original
          `!readOnly` gate (no visibility change); the advisor New Client button,
          previously its own footer, is folded into this single footer. */}
      {(!readOnly || onNewClient) && (
        <SidebarFooter>
          {!readOnly && (
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location === "/settings"}
                  data-testid="link-settings"
                >
                  <Link href="/settings">
                    <Settings className="h-4 w-4" />
                    <span>Settings</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          )}
          {onNewClient && (
            <Button onClick={onNewClient} variant="outline" className="w-full gap-2" data-testid="button-new-client">
              <Plus className="h-4 w-4" />
              New Client
            </Button>
          )}
        </SidebarFooter>
      )}

      <DeleteConfirmDialog
        open={!!hideCategoryTarget}
        onOpenChange={(open) => { if (!open) setHideCategoryTarget(null); }}
        title={`Hide "${hideCategoryTarget?.name}"?`}
        description="This category will be hidden from your sidebar and dashboard. You can restore it at any time from the Hidden Categories section at the bottom of the dashboard."
        onConfirm={() => {
          if (hideCategoryTarget) hideCategoryMutation.mutate(hideCategoryTarget.id);
        }}
        isPending={hideCategoryMutation.isPending}
        confirmLabel="Hide Category"
        cancelLabel="Keep Visible"
        testIdPrefix="sidebar-hide-category"
      />
    </Sidebar>
  );
}
